<template>
  <div class="drive-file-list">
    <!-- List View -->
    <div v-if="viewMode === 'list'" class="c-common-box-shadow c-bg-white">
      <table class="table table-hover drive-table">
        <thead class="drive-thead-dark">
          <tr>
            <th style="width: 40px;"></th>
            <th class="drive-sortable" @click="toggleSort('name')">
              Name <i v-if="sortKey === 'name'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
            </th>
            <th class="drive-sortable" style="width: 100px;" @click="toggleSort('size')">
              Size <i v-if="sortKey === 'size'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
            </th>
            <th class="drive-sortable" style="width: 130px;" @click="toggleSort('createdBy')">
              Created By <i v-if="sortKey === 'createdBy'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
            </th>
            <th class="drive-sortable" style="width: 200px;" @click="toggleSort('createdTime')">
              Created <i v-if="sortKey === 'createdTime'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
            </th>
            <th class="drive-sortable" style="width: 200px;" @click="toggleSort('lastModifiedBy')">
              Updated By <i v-if="sortKey === 'lastModifiedBy'" :class="sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true" style="opacity: 0.4;"></i>
            </th>
            <th :style="{ width: isAdmin ? '180px' : '100px' }">Actions</th>
          </tr>
        </thead>
        <tbody>
          <DriveFileRow
            v-for="file in sortedFiles"
            :key="file.id"
            :file="file"
            :is-admin="isAdmin"
            @open-folder="$emit('open-folder', $event)"
            @preview-file="$emit('preview-file', $event)"
            @download-file="$emit('download-file', $event)"
            @share-file="$emit('share-file', $event)"
            @rename-file="$emit('rename-file', $event)"
            @move-file="$emit('move-file', $event)"
            @delete-file="$emit('delete-file', $event)"
            @toggle-restriction="$emit('toggle-restriction', $event)"
            @toggle-visibility="$emit('toggle-visibility', $event)"
            @set-deadline="$emit('set-deadline', $event)"
            @edit-text-file="$emit('edit-text-file', $event)"
          />
        </tbody>
      </table>
    </div>

    <!-- Grid View -->
    <div v-if="viewMode === 'grid'" class="drive-grid row" @click="closeMenu">
      <div
        v-for="file in sortedFiles"
        :key="file.id"
        class="col-md-2 col-sm-3 col-xs-4 drive-grid-item"
      >
        <div
          class="drive-grid-card c-common-box-shadow"
          @dblclick="onDblClick(file)"
          @contextmenu.prevent="openContextMenu(file, $event)"
        >
          <!-- Three-dot menu button -->
          <button
            class="drive-grid-menu-btn"
            @click.stop="openContextMenu(file, $event)"
            title="Actions"
          >
            <i class="fa fa-ellipsis-v" aria-hidden="true"></i>
          </button>
          <div class="drive-grid-icon">
            <i :class="getGridIconClass(file)" :style="{ color: getIconColor(file) }" aria-hidden="true"></i>
          </div>
          <div class="drive-grid-name" :title="file.name">
            {{ file.name }}
          </div>
          <div v-if="file.deadline" class="drive-grid-deadline" :style="{ color: getDeadlineColor(file) }">
            <i class="fa fa-calendar-times-o"></i>
            {{ file.deadline.daysRemaining <= 0 ? 'Due today' : file.deadline.daysRemaining + 'd' }}
          </div>
        </div>
      </div>

      <!-- Context menu dropdown -->
      <div
        v-if="contextMenu.visible"
        class="drive-context-menu c-common-box-shadow"
        :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
        @click.stop
      >
        <template v-if="contextMenu.file">
          <a
            v-if="contextMenu.file.isFolder"
            class="drive-ctx-item"
            @click="ctxAction('open-folder', contextMenu.file)"
          >
            <i class="fa fa-folder-open-o"></i> Open
          </a>
          <a
            v-if="!contextMenu.file.isFolder && contextMenu.file.webViewLink && isNativeGoogleDoc(contextMenu.file)"
            class="drive-ctx-item"
            :href="contextMenu.file.webViewLink"
            target="_blank"
            rel="noopener noreferrer"
            @click="closeMenu"
          >
            <i class="fa fa-pencil-square-o"></i> Edit
          </a>
          <a
            v-if="!contextMenu.file.isFolder && isEditableTextFile(contextMenu.file)"
            class="drive-ctx-item"
            @click="ctxAction('edit-text-file', contextMenu.file)"
          >
            <i class="fa fa-pencil-square-o" style="color: #67c23a;"></i> Edit
          </a>
          <a
            v-if="!contextMenu.file.isFolder"
            class="drive-ctx-item"
            @click="ctxAction('preview-file', contextMenu.file)"
          >
            <i class="fa fa-eye"></i> Preview
          </a>
          <a
            v-if="!contextMenu.file.isFolder && (!contextMenu.file.downloadRestricted || isAdmin)"
            class="drive-ctx-item"
            @click="ctxAction('download-file', contextMenu.file)"
          >
            <i class="fa fa-cloud-download"></i> Download
          </a>
          <template v-if="isAdmin">
            <div class="drive-ctx-divider"></div>
            <a v-if="!contextMenu.file.isFolder" class="drive-ctx-item" @click="ctxAction('set-deadline', contextMenu.file)">
              <i class="fa fa-calendar-times-o"></i>
              {{ contextMenu.file.deadline ? 'Edit Deadline' : 'Set Deadline' }}
            </a>
            <a class="drive-ctx-item" @click="ctxAction('toggle-restriction', contextMenu.file)">
              <i :class="contextMenu.file.downloadRestricted ? 'fa fa-lock' : 'fa fa-unlock-alt'"></i>
              {{ contextMenu.file.downloadRestricted ? 'Remove Download Restriction' : 'Restrict Downloads' }}
            </a>
            <a class="drive-ctx-item" @click="ctxAction('toggle-visibility', contextMenu.file)">
              <i :class="contextMenu.file.visibilityRestricted ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
              {{ contextMenu.file.visibilityRestricted ? 'Show to Non-Admins' : 'Hide from Non-Admins' }}
            </a>
            <a class="drive-ctx-item" @click="ctxAction('share-file', contextMenu.file)">
              <i class="fa fa-share-alt"></i> Share
            </a>
            <a class="drive-ctx-item" @click="ctxAction('rename-file', contextMenu.file)">
              <i class="fa fa-pencil"></i> Rename
            </a>
            <a class="drive-ctx-item" @click="ctxAction('move-file', contextMenu.file)">
              <i class="fa fa-arrows"></i> Move
            </a>
            <div class="drive-ctx-divider"></div>
            <a class="drive-ctx-item drive-ctx-danger" @click="ctxAction('delete-file', contextMenu.file)">
              <i class="fa fa-trash-o"></i> Delete
            </a>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import DriveFileRow from "./DriveFileRow.vue";
import { getFileIconInfo } from "./driveIcons";

export default {
  name: "DriveFileList",
  components: { DriveFileRow },
  props: {
    files: {
      type: Array,
      default: function () {
        return [];
      },
    },
    viewMode: { type: String, default: "list" },
    isAdmin: { type: Boolean, default: false },
  },
  data: function () {
    return {
      contextMenu: { visible: false, file: null, x: 0, y: 0 },
      sortKey: 'name',
      sortDir: 'asc',
    };
  },
  mounted: function () {
    var self = this;
    this._closeMenuHandler = function () { self.closeMenu(); };
    document.addEventListener("click", this._closeMenuHandler);
  },
  beforeDestroy: function () {
    document.removeEventListener("click", this._closeMenuHandler);
  },
  computed: {
    sortedFiles: function () {
      var key = this.sortKey;
      var dir = this.sortDir;
      return this.files.slice().sort(function (a, b) {
        // Folders always first
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        var va, vb;
        if (key === 'size') {
          va = parseInt(a.size) || 0;
          vb = parseInt(b.size) || 0;
        } else {
          va = (a[key] || '').toString().toLowerCase();
          vb = (b[key] || '').toString().toLowerCase();
        }
        if (va < vb) return dir === 'asc' ? -1 : 1;
        if (va > vb) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    },
  },
  methods: {
    toggleSort: function (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },
    isNativeGoogleDoc: function (file) {
      var mime = (file.mimeType || "");
      return mime.indexOf("application/vnd.google-apps.") === 0 && !file.isFolder;
    },
    isEditableTextFile: function (file) {
      var mime = (file.mimeType || "").toLowerCase();
      var editableMimes = [
        "text/plain", "text/markdown", "text/html", "text/csv", "text/css",
        "application/json", "application/xml", "application/javascript",
      ];
      if (editableMimes.indexOf(mime) !== -1) return true;
      var ext = (file.fileExtension || file.name.split(".").pop() || "").toLowerCase();
      var editableExts = ["txt", "md", "log", "json", "csv", "xml", "html", "css", "js"];
      return editableExts.indexOf(ext) !== -1;
    },
    getGridIconClass: function (file) {
      return getFileIconInfo(file).iconClass + " fa-3x";
    },
    getIconColor: function (file) {
      return getFileIconInfo(file).color;
    },
    getDeadlineColor: function (file) {
      if (!file.deadline) return "#ccc";
      var days = file.deadline.daysRemaining;
      if (days <= 0) return "#c0392b";
      if (days <= 3) return "#e67e22";
      if (days <= 7) return "#f1c40f";
      return "#27ae60";
    },
    onDblClick: function (file) {
      if (file.isFolder) {
        this.$emit("open-folder", file);
      } else {
        this.$emit("preview-file", file);
      }
    },
    openContextMenu: function (file, event) {
      this.contextMenu.file = file;
      this.contextMenu.x = event.clientX;
      this.contextMenu.y = event.clientY;
      this.contextMenu.visible = true;

      // Ensure menu stays in viewport
      var self = this;
      this.$nextTick(function () {
        var el = self.$el.querySelector(".drive-context-menu");
        if (!el) return;
        var rect = el.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          self.contextMenu.x = window.innerWidth - rect.width - 8;
        }
        if (rect.bottom > window.innerHeight) {
          self.contextMenu.y = window.innerHeight - rect.height - 8;
        }
      });
    },
    closeMenu: function () {
      this.contextMenu.visible = false;
    },
    ctxAction: function (action, file) {
      this.closeMenu();
      this.$emit(action, file);
    },
  },
};
</script>

<style scoped>
.drive-table {
  margin-bottom: 0;
}
.drive-thead-dark th {
  background: #333;
  color: #fff;
  border-color: #444;
}
.drive-sortable {
  cursor: pointer;
  user-select: none;
}
.drive-sortable:hover {
  background: #444;
}
.drive-sortable i {
  margin-left: 4px;
}
.drive-grid-item {
  padding: 8px;
}
.drive-grid-card {
  position: relative;
  background: #fff;
  border-radius: 4px;
  padding: 15px 10px;
  text-align: center;
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.drive-grid-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.drive-grid-icon {
  margin-bottom: 8px;
}
.drive-grid-name {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drive-grid-deadline {
  font-size: 10px;
  margin-top: 4px;
}

/* Three-dot menu button */
.drive-grid-menu-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: none;
  border: none;
  color: #999;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 3px;
  opacity: 0;
  transition: opacity 0.15s;
}
.drive-grid-card:hover .drive-grid-menu-btn {
  opacity: 1;
}
.drive-grid-menu-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #333;
}

/* Context menu */
.drive-context-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 4px;
  min-width: 160px;
  padding: 4px 0;
}
.drive-ctx-item {
  display: block;
  padding: 8px 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}
.drive-ctx-item:hover {
  background: #f0f5ff;
  color: #337ab7;
}
.drive-ctx-item i {
  width: 18px;
  text-align: center;
  margin-right: 8px;
}
.drive-ctx-danger:hover {
  background: #fff1f0;
  color: #c0392b;
}
.drive-ctx-divider {
  height: 1px;
  background: #eee;
  margin: 4px 0;
}
</style>
